import CryptoJS from 'crypto-js';
import { SECRET_KEY } from 'api/constants';

export const TOKEN_COOKIE = 'rtpi_token';
const LEGACY_TOKEN_STORAGE_KEY = 'token';
const TOKEN_MAX_AGE = 60 * 60 * 24 * 30;

function encryptToken(token: string) {
  return CryptoJS.AES.encrypt(token, SECRET_KEY).toString();
}

function decryptToken(value?: string) {
  if (!value) {
    return undefined;
  }

  try {
    const decrypted = CryptoJS.AES.decrypt(value, SECRET_KEY).toString(
      CryptoJS.enc.Utf8
    );
    return decrypted || undefined;
  } catch {
    return undefined;
  }
}

export function parseCookieHeader(header?: string) {
  if (!header) {
    return {} as Record<string, string>;
  }

  return header.split(';').reduce<Record<string, string>>((acc, part) => {
    const [rawKey, ...rawValue] = part.trim().split('=');
    if (!rawKey) {
      return acc;
    }

    try {
      acc[rawKey] = decodeURIComponent(rawValue.join('='));
    } catch {
      acc[rawKey] = rawValue.join('=');
    }

    return acc;
  }, {});
}

export function getTokenFromRequest(req?: { headers?: { cookie?: string } }) {
  const cookies = parseCookieHeader(req?.headers?.cookie);
  return decryptToken(cookies[TOKEN_COOKIE]);
}

export function getClientToken() {
  if (typeof document === 'undefined') {
    return undefined;
  }

  const cookies = parseCookieHeader(document.cookie);
  const cookieToken = decryptToken(cookies[TOKEN_COOKIE]);

  if (cookieToken) {
    return cookieToken;
  }

  return migrateLegacyLocalStorageToken();
}

export function setClientToken(token: string) {
  if (typeof document === 'undefined') {
    return;
  }

  const encrypted = encryptToken(token);
  document.cookie = [
    `${TOKEN_COOKIE}=${encodeURIComponent(encrypted)}`,
    'path=/',
    `max-age=${TOKEN_MAX_AGE}`,
    'SameSite=Lax',
    getSecureCookieAttribute(),
  ]
    .filter(Boolean)
    .join('; ');
}

export function clearClientToken() {
  if (typeof document === 'undefined') {
    return;
  }

  document.cookie = [
    `${TOKEN_COOKIE}=`,
    'path=/',
    'max-age=0',
    'SameSite=Lax',
    getSecureCookieAttribute(),
  ]
    .filter(Boolean)
    .join('; ');

  try {
    window.localStorage?.removeItem(LEGACY_TOKEN_STORAGE_KEY);
  } catch {
    // localStorage can be unavailable in restricted browser contexts.
  }
}

function getSecureCookieAttribute() {
  if (typeof window === 'undefined') {
    return '';
  }

  return window.location.protocol === 'https:' ? 'Secure' : '';
}

function migrateLegacyLocalStorageToken() {
  try {
    const legacyValue = window.localStorage?.getItem(LEGACY_TOKEN_STORAGE_KEY);
    const legacyToken = decryptToken(legacyValue ?? undefined);

    if (!legacyToken) {
      return undefined;
    }

    setClientToken(legacyToken);
    window.localStorage?.removeItem(LEGACY_TOKEN_STORAGE_KEY);
    return legacyToken;
  } catch {
    return undefined;
  }
}
